import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldCheck, Shield, Check, AlertCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CheckResult {
  key: string;
  label: string;
  passed: boolean;
  tooltip: string;
}

type BannerState = 'loading' | 'pass' | 'fail' | 'error';

const REFRESH_INTERVAL = 5 * 60 * 1000;

const tooltipStyle: React.CSSProperties = {
  maxWidth: 260,
  fontSize: 12,
  background: '#02075d',
  color: '#ffffff',
  borderRadius: 6,
  padding: '8px 12px',
  lineHeight: 1.5,
  border: 'none',
};

export function SystemIntegrityIndicator() {
  const { profile } = useAuth();
  const [checks, setChecks] = useState<CheckResult[]>([]);
  const [state, setState] = useState<BannerState>('loading');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  const runChecks = useCallback(async () => {
    if (!profile?.clinic_id) return;

    try {
      const [check1, check2, check3, check4] = await Promise.all([
        supabase
          .from('policy_versions')
          .select('id', { count: 'exact', head: true })
          .eq('version_status', 'approved')
          .or('approved_at.is.null,approved_by.is.null'),
        supabase
          .from('policy_acknowledgements')
          .select('id', { count: 'exact', head: true })
          .is('acknowledged_at', null),
        Promise.all([
          supabase
            .from('policies')
            .select('id')
            .eq('status', 'Approved')
            .eq('is_archived', false),
          supabase
            .from('policy_versions')
            .select('policy_id')
            .eq('version_status', 'approved'),
        ]),
        supabase
          .from('policy_acknowledgements')
          .select('id')
          .limit(1),
      ]);

      const c1Passed = !check1.error && (check1.count ?? 0) === 0;
      const c2Passed = !check2.error && (check2.count ?? 0) === 0;

      const [approvedPolicies, approvedVersions] = check3;
      let c3Passed = true;
      if (approvedPolicies.error || approvedVersions.error) {
        c3Passed = false;
      } else {
        const versionPolicyIds = new Set(
          (approvedVersions.data ?? []).map((v) => v.policy_id)
        );
        const missing = (approvedPolicies.data ?? []).filter(
          (p) => !versionPolicyIds.has(p.id)
        );
        c3Passed = missing.length === 0;
      }

      const c4Passed = !check4.error;

      const results: CheckResult[] = [
        {
          key: 'policy_records',
          label: 'Policy Records',
          passed: c1Passed,
          tooltip: c1Passed
            ? 'All approved versions have complete approval metadata'
            : 'Some approved versions are missing approval metadata',
        },
        {
          key: 'acknowledgements',
          label: 'Acknowledgements',
          passed: c2Passed,
          tooltip: c2Passed
            ? 'All acknowledgement records have valid timestamps'
            : 'Some acknowledgement records have missing timestamps',
        },
        {
          key: 'version_history',
          label: 'Version History',
          passed: c3Passed,
          tooltip: c3Passed
            ? 'All approved policies have at least one approved version'
            : 'Some approved policies are missing approved version records',
        },
        {
          key: 'compliance_tracking',
          label: 'Compliance Tracking',
          passed: c4Passed,
          tooltip: c4Passed
            ? 'Compliance tracking system is accessible'
            : 'Unable to access compliance tracking data',
        },
      ];

      setChecks(results);
      setState(results.every((c) => c.passed) ? 'pass' : 'fail');
    } catch {
      setState('error');
    }
  }, [profile?.clinic_id]);

  useEffect(() => {
    runChecks();
    const interval = setInterval(runChecks, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [runChecks]);

  if (!profile?.clinic_id) return null;

  const wrapperClass = "hidden md:block";

  const isFail = state === 'fail';
  const failCount = checks.filter((c) => !c.passed).length;

  const containerBaseStyle: React.CSSProperties = {
    background: 'transparent',
    padding: '12px 0',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 16,
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(-4px)',
    transition: 'opacity 0.4s ease, transform 0.4s ease',
  };

  if (state === 'loading') {
    return (
      <div className={wrapperClass}>
      <div style={{ ...containerBaseStyle, opacity: mounted ? 1 : 0 }} className="animate-pulse border-b border-border dark:border-white/10">
        {/* Teal accent bar */}
        <div className="dark:bg-teal-400" style={{ width: 4, height: 32, background: '#339999', borderRadius: 999, flexShrink: 0, alignSelf: 'center' }} />
        {/* Left placeholder */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: '#f1f5f9', border: '1px solid #e2e8f0' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ width: 100, height: 10, borderRadius: 4, background: '#e2e8f0' }} />
            <div style={{ width: 70, height: 8, borderRadius: 4, background: '#e2e8f0' }} />
          </div>
        </div>
        {/* Divider */}
        <div className="dark:!bg-white/20" style={{ width: 1, height: 28, background: '#e2e8f0', flexShrink: 0 }} />
        {/* Right placeholder pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {[90, 110, 100, 120].map((w, i) => (
            <div key={i} style={{ width: w, height: 24, borderRadius: 999, background: '#f1f5f9', border: '1px solid #e2e8f0' }} />
          ))}
        </div>
      </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={wrapperClass}>
      <div style={containerBaseStyle} className="border-b border-border dark:border-white/10">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#f8fafc', border: '1px solid #e2e8f0',
          }}>
            <Shield size={14} color="#94a3b8" />
          </div>
          <div>
            <div className="dark:!text-white" style={{ fontSize: 12, fontWeight: 600, color: '#02075d', letterSpacing: '-0.01em' }}>System Integrity</div>
            <div style={{ fontSize: 10, fontWeight: 500, color: '#94a3b8' }}>Status unavailable</div>
          </div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
    <TooltipProvider delayDuration={200}>
      <div style={{ ...containerBaseStyle, position: 'relative', zIndex: 50 }} className="border-b border-border dark:border-white/10">
        {/* Teal accent bar */}
        <div className="dark:!bg-teal-400" style={{ width: 4, height: 32, background: '#339999', borderRadius: 999, flexShrink: 0, opacity: 1, alignSelf: 'center' }} />

        {/* Left section */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, cursor: 'default' }}>
              <div
                className={isFail
                  ? 'dark:!bg-amber-950 dark:!border-amber-800'
                  : 'dark:!bg-green-950 dark:!border-green-800'}
                style={{
                  width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isFail
                    ? 'linear-gradient(135deg, #fffbeb, #fef3c7)'
                    : 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                  border: `1px solid ${isFail ? '#fde68a' : '#bbf7d0'}`,
                }}
              >
                {isFail
                  ? <Shield size={14} color="#d97706" />
                  : <ShieldCheck size={14} color="#339999" />}
              </div>
              <div>
                <div className="dark:!text-white" style={{ fontSize: 12, fontWeight: 600, color: '#02075d', letterSpacing: '-0.01em' }}>
                  System Integrity
                </div>
                <div style={{ fontSize: 10, fontWeight: 500, color: isFail ? '#d97706' : '#339999' }}>
                  {isFail
                    ? `${failCount} check${failCount !== 1 ? 's' : ''} require attention`
                    : 'All records verified'}
                </div>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" style={{ ...tooltipStyle, zIndex: 9999 }} className="border-0 z-[9999]">
            Veracord automatically verifies the integrity of your compliance records every 5 minutes. All four checks must pass to confirm your system of record is intact and audit-ready.
          </TooltipContent>
        </Tooltip>

        {/* Divider */}
        <div className="dark:!bg-white/20" style={{ width: 1, height: 28, background: '#e2e8f0', flexShrink: 0 }} />

        {/* Right section — pills */}
        <div className="flex items-center gap-1.5 flex-wrap" style={{ justifyContent: 'flex-end', flex: 1 }}>
          {checks.map((check) => (
            <Tooltip key={check.key}>
              <TooltipTrigger asChild>
                <div
                  className={check.passed
                    ? 'dark:!bg-green-950 dark:!border-green-800 dark:!text-green-400'
                    : 'dark:!bg-amber-950 dark:!border-amber-800 dark:!text-amber-400'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                    fontSize: 10, fontWeight: 500, cursor: 'default',
                    background: check.passed ? '#f0fdf4' : '#fffbeb',
                    border: `1px solid ${check.passed ? '#bbf7d0' : '#fde68a'}`,
                    color: check.passed ? '#15803d' : '#b45309',
                  }}
                >
                  {check.passed
                    ? <Check size={11} color="#16a34a" />
                    : <AlertCircle size={11} color="#d97706" />}
                  <span>{check.label}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" style={{ ...tooltipStyle, zIndex: 9999 }} className="border-0 z-[9999]">
                {check.tooltip}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>
    </TooltipProvider>
    </div>
  );
}
